import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { rgba } from "polished";
import { FiImage, FiEye, FiClock, FiChevronDown, FiChevronUp, FiDownload } from "react-icons/fi";
import { pdf } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import { ParsedRecord } from "../../types/records";
import { RecordPDFDocument } from "../pdf/RecordPDFDocument";
import { DeviceInfo } from "./DeviceInfo";
import { JsonViewModal } from "./JsonViewModal";

interface RecordCardProps {
  record: ParsedRecord;
  index: number;
  formatDate: (dateString: string) => string;
  formatKey: (key: string) => string;
  isExpanded?: boolean;
  canBeExpanded?: boolean;
  showJsonLink?: boolean;
  blockchainData?: {
    index: number;
    hash: string;
    timestamp?: string;
  };
}

interface RecordInfoContainerProps {
  expanded: boolean;
}

const Card = styled.div`
  padding: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background-color: white;
  /* box-shadow: ${({ theme }) => theme.shadows.md}; */
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    /* border-color: ${({ theme }) => theme.colors.verae[200]}; */
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  position: relative;
  z-index: 1;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 576px) {
    padding: 14px 18px;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: ${({ theme }) => theme.colors.verae[500]};
    font-size: 16px;
  }

  span {
    font-weight: 500;
    letter-spacing: 0;
    color: ${({ theme }) => theme.colors.primary};
    margin-left: 2px;
  }
`;

const Timestamp = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${({ theme }) => theme.colors.background};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  svg {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const RecordInfoContainer = styled.div<RecordInfoContainerProps>`
  padding: 24px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  max-height: ${({ expanded }) => (expanded ? "none" : "150px")};
  transition: max-height 0.3s ease;

  @media (max-width: 576px) {
    padding: 20px;
  }
`;

RecordInfoContainer.defaultProps = {
  expanded: false,
};

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.verae[500]};
  cursor: pointer;
  padding: 8px 0;
  margin: 0 auto;
  transition: color 0.2s ease;
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.verae[600]};
  }

  svg {
    font-size: 14px;
  }
`;

const ImageContainer = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => rgba(theme.colors.verae[50], 0.5)};
  position: relative;
  overflow: hidden;
  flex: 1;

  .no-image-wrapper {
    width: 100%;
    height: 100%;
    min-height: 220px;
    max-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.verae[50]};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${({ theme }) =>
        `radial-gradient(circle at center, 
          ${theme.colors.verae[100]} 0%, 
          ${theme.colors.verae[50]} 70%)`};
      z-index: 1;
    }

    .icon-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
      border: 1px solid ${({ theme }) => rgba(theme.colors.secondary, 0.15)};
      border-radius: 50%;
      width: 80px;
      height: 80px;
      margin-bottom: 16px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      z-index: 2;
    }

    svg {
      font-size: 36px;
      color: white;
    }

    p {
      padding: 0;
      color: ${({ theme }) => rgba(theme.colors.text, 0.7)};
      font-size: 15px;
      font-weight: 400;
      z-index: 2;
    }
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    min-height: 220px;
    max-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    /* background-color: ${({ theme }) => rgba(0, 0, 0, 0.1)}; */
  }

  img {
    /* width: 100%; */
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    padding: 16px;
  }

  &:hover img {
    transform: scale(1.03);
  }

  .image-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    text-align: center;
    letter-spacing: -0.2px;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: white;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: auto;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 18px;
  }
`;

const JsonLink = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.verae[600]};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ theme }) => theme.colors.verae[50]};
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.verae[100]};
    color: ${({ theme }) => theme.colors.verae[700]};
  }

  svg {
    font-size: 13px;
  }
`;

const PdfLink = styled(JsonLink)``;

const RecordCard: React.FC<RecordCardProps> = ({
  record,
  index,
  formatDate,
  formatKey,
  blockchainData,
  showJsonLink = true,
  isExpanded = false,
  canBeExpanded = true,
}) => {
  const navigate = useNavigate();
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(isExpanded);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Consolidated data object for JSON / PDF – memoized to avoid regenerating each render
  const jsonData = useMemo(
    () => ({
      deviceData: {
        imageHash: record.hash,
        imageBase64: record.image,
        ...Object.fromEntries(
          Object.entries(record).filter(
            ([key]) => key !== "hash" && key !== "originalTimestamp" && key !== "image" && key !== "timestamp"
          )
        ),
      },
      // blockchainData,
    }),
    [record]
  );

  // Get device model and make for display
  const deviceName =
    record.deviceInfo?.make && record.deviceInfo?.model
      ? `${record.deviceInfo.make} ${record.deviceInfo.model}`
      : record.deviceInfo?.title || "Unknown Device";

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (
      (e.target as HTMLElement).closest(".action-button") ||
      (e.target as HTMLElement).closest(".show-more-button")
    ) {
      return;
    }

    // Check if we're already on the RecordDetailView page
    const currentPath = window.location.pathname;
    if (
      currentPath.startsWith("/record/") &&
      currentPath !== "/record/add" &&
      currentPath !== "/record/manual" &&
      currentPath !== "/record/verify"
    ) {
      return;
    }

    // Navigate to record detail view
    const recordHash = blockchainData?.hash || record.hash;
    if (recordHash) {
      const encodedHash = encodeURIComponent(recordHash);
      navigate(`/record/${encodedHash}`);
    }
  };

  // Memoize pdf document and filename to avoid heavy regeneration
  const pdfDocument = useMemo(
    () => (
      <RecordPDFDocument
        data={jsonData}
        title={`Record #${blockchainData?.index || index || "000"} - ${deviceName}`}
      />
    ),
    [jsonData, blockchainData?.index, index, deviceName]
  );

  const pdfFileName = useMemo(
    () => `record_${blockchainData?.hash || record.hash}.pdf`,
    [blockchainData?.hash, record.hash]
  );

  const handlePdfDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setGeneratingPdf(true);
    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfFileName;
      a.click();
      URL.revokeObjectURL(url);
      setGeneratingPdf(false);
    } catch (err) {
      console.error("PDF generation failed", err);
      setGeneratingPdf(false);
    }
  };

  return (
    <Card onClick={handleCardClick} style={{ cursor: "pointer" }}>
      <Header>
        <Title>
          {/* <FiBox /> */}
          Record <span>#{blockchainData?.index || "000"}</span>
        </Title>
        <Timestamp>
          <FiClock />
          {formatDate(blockchainData?.timestamp || "") || "Unknown Date"}
        </Timestamp>
      </Header>

      <Content>
        <ImageContainer>
          {(() => {
            if (record?.image) {
              return (
                <div className="image-wrapper">
                  <img src={record.image} alt={deviceName} />
                  <div className="image-caption">{deviceName}</div>
                </div>
              );
            }

            // @ts-ignore
            if (record["purchase_date"] || record.deviceInfo?.purchase_date) {
              return (
                <div className="image-wrapper">
                  <img
                    src={"/evercycle-logo.svg"}
                    alt={"Evercycle"}
                    style={{ width: "auto", height: "75px" }}
                  />
                  <div className="image-caption">From Evercycle</div>
                </div>
              );
            }

            return (
              <div className="no-image-wrapper">
                <div className="icon-container">
                  <FiImage />
                </div>
                <p>No image available</p>
              </div>
            );
          })()}
        </ImageContainer>

        <RecordInfoContainer expanded={expanded}>
          <DeviceInfo
            deviceInfo={record.deviceInfo ? record.deviceInfo : record}
            formatKey={formatKey}
            blockchainData={blockchainData}
          />
          {!expanded && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "10px",
                background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.05))",
              }}
            />
          )}
        </RecordInfoContainer>

        {canBeExpanded ? (
          <ShowMoreButton
            className="show-more-button"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
          >
            {expanded ? (
              <>
                Show Less <FiChevronUp />
              </>
            ) : (
              <>
                Show More <FiChevronDown />
              </>
            )}
          </ShowMoreButton>
        ) : (
          <ShowMoreButton className="show-more-button">
            <div style={{ height: "20px" }} />
          </ShowMoreButton>
        )}
      </Content>

      {record.hash && showJsonLink && (
        <Footer>
          {/* <HashDisplay hash={record.hash} label="Hash" /> */}
          <JsonLink
            className="action-button"
            onClick={(e) => {
              e.stopPropagation();
              setJsonModalOpen(true);
            }}
          >
            <FiEye /> View JSON
          </JsonLink>

          <PdfLink className="action-button" onClick={handlePdfDownload}>
            <FiDownload /> {generatingPdf ? "Generating..." : "PDF"}
          </PdfLink>
        </Footer>
      )}

      <JsonViewModal
        isOpen={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={jsonData}
        title={`Record #${blockchainData?.index || index || "000"} - ${deviceName}`}
      />
    </Card>
  );
};

export { RecordCard };
